import { makeStyles } from '@material-ui/core';
import styles from 'assets/jss/material-dashboard-pro-react/views/prepaidLoyalty/addNumbersDialogStyle';
import CustomButton from 'components/CustomButtons/Button';
import CustomInput from 'components/CustomInput/CustomInput';
import CustomTag from 'components/CustomTag/CustomTag';
import { translate } from 'components/Translation';
import React, { useState } from 'react';
import { NumbersService } from 'services/numbers/numbers';
import { PHONE_NUMBER_LENGTH } from 'utils/const/numbers';
import { concatMap, catchError } from 'rxjs/operators';
import { AccountType } from 'utils/api/models/accounts/accountType';
import { SearchService } from 'services/search/search';
import { EMPTY, throwError } from 'rxjs';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IAddAnyNumber {
	numbers: string[];
	setNumbers: React.Dispatch<React.SetStateAction<string[]>>;
	loading: boolean;
	setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const AddAnyNumber = ({ numbers, setNumbers, loading, setLoading }: IAddAnyNumber) => {
	const [number, setNumber] = useState<string>('');
	const [error, setError] = useState<string>('');
	const classes = useStyles();

	const isAddCustomNumberDisabled = number.length !== PHONE_NUMBER_LENGTH || loading || !!error;

	const addNumber = () => {
		setNumbers((prevNumbers) => [...prevNumbers, number]);
		setNumber('');
	};

	const onError = (errorMessage?: string) => {
		setError(errorMessage || translate('prepaid.loyalty.add.any.number.can.not.add'));
		setLoading(false);
	};

	const checkNumber = (number: string) => {
		setLoading(true);
		const searchResponse = NumbersService.getAccountNumberInfo(number).pipe(
			concatMap(({ accountType }) => {
				if (accountType === AccountType.MOBILE) {
					if (!!numbers.find((numb) => numb === number)) {
						onError(translate('prepaid.loyalty.add.any.number.exists'));
					} else {
						return SearchService.searchNumbersAndUsersRestPost(number);
					}
				} else {
					onError(translate('prepaid.loyalty.add.any.number.not.mobile'));
				}
				return EMPTY;
			}),
			catchError((error) => {
				onError();
				return throwError(error);
			})
		);
		searchResponse.subscribe(({ numbers }) => {
			if (numbers.length > 0) {
				onError(translate('prepaid.loyalty.add.any.number.number.in.organization'));
			} else {
				setLoading(false);
			}
		});
	};

	const removeFromNumbers = (removedNumber: string) => {
		if (removedNumber === number) {
			setError('');
		}
		setNumbers((oldNumbers: Array<string>) => {
			return oldNumbers.filter((oldNumber) => oldNumber !== removedNumber);
		});
	};

	return (
		<>
			<div className={classes.addAnyNumberInputWrapper}>
				<CustomInput
					disableHover
					inputProps={{
						value: number,
						onChange: (event: React.FormEvent<HTMLInputElement>) => {
							let value = (event.target as HTMLInputElement).value;
							value = value.replace(/\D/g, '').substr(0, PHONE_NUMBER_LENGTH);
							setNumber(value);
							setError('');
							if (value.length === PHONE_NUMBER_LENGTH) {
								checkNumber(value);
							}
						},
						maxLength: PHONE_NUMBER_LENGTH,
					}}
					labelText={translate('prepaid.loyalty.add.any.number')}
					formControlProps={{
						fullWidth: true,
					}}
					helperText={error}
					error={!!error}
					disabled={loading}
					success={!isAddCustomNumberDisabled}
				/>
				<div className={classes.addNumberBtnWrapper}>
					<CustomButton disabled={isAddCustomNumberDisabled} onClick={addNumber} color="tertiary-light">
						{translate('add')}
					</CustomButton>
				</div>
			</div>
			<div>
				{numbers.map((number) => (
					<div key={number} className={classes.customNumber}>
						<CustomTag
							className={`custom-number-tag ${classes.numberTag}`}
							onClick={() => removeFromNumbers(number)}
							label={number}
						/>
					</div>
				))}
			</div>
		</>
	);
};

export default AddAnyNumber;
